// ── Schulkarten drucken (M27 T10) ────────────────────────────────────────────
// Eine CR80-Karte (85,6 x 54 mm) je Seite, gedruckt über den Browser — keine
// PDF-Bibliothek. Auf der Karte stehen NIE die PIN und NIE der volle Schulname.
// Bewusst ohne relative Imports: der Unit-Test lädt diese Datei direkt mit node.

import qrcode from 'qrcode-generator'

/** Das druckbare Minimum einer Karte (strukturell kompatibel zu SchoolCard). */
export interface PrintableCard {
  kuerzel: string | null
  bezeichnung: string
  chip: string
}

export interface CardPrintModel {
  kuerzel: string
  bezeichnung: string
  chip: string
  qrContent: string
}

/** Inhalt des QR-Codes: führt auf der Anmeldeseite direkt zum PIN-Schritt. */
export function cardQrContent(chip: string): string {
  return `https://mein.mammascanteen.de/mitarbeiter/anmelden#k=${chip}`
}

/** Alles, was auf die Karte gedruckt wird — und nur das. */
export function cardPrintModel(card: PrintableCard): CardPrintModel {
  return {
    kuerzel: card.kuerzel ?? '',
    bezeichnung: card.bezeichnung,
    chip: card.chip,
    qrContent: cardQrContent(card.chip),
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)
}

function qrSvg(content: string): string {
  const qr = qrcode(0, 'M')
  qr.addData(content)
  qr.make()
  return qr.createSvgTag({ cellSize: 2, margin: 0, scalable: true })
}

const CSS = `
  @page { size: 85.6mm 54mm; margin: 0 }
  * { box-sizing: border-box }
  html, body { margin: 0; padding: 0; background: #fff }
  body { font-family: Inter, Arial, sans-serif; color: #1c2530; -webkit-print-color-adjust: exact; print-color-adjust: exact }
  .card { width: 85.6mm; height: 54mm; position: relative; overflow: hidden; page-break-after: always; break-after: page }
  .card:last-child { page-break-after: auto; break-after: auto }
  .band { height: 9mm; background: #000; color: #fff; display: flex; align-items: center; justify-content: space-between; padding: 0 4mm }
  .band b { font-size: 9pt; letter-spacing: 0.22em }
  .band img { height: 6mm; width: auto; background: #fff; border-radius: 1mm; padding: 0.6mm 1mm }
  .body { display: flex; justify-content: space-between; align-items: center; padding: 3.5mm 4mm 0 4mm; height: 45mm }
  .txt { min-width: 0; padding-right: 3mm }
  .name { font-size: 13pt; font-weight: 700; line-height: 1.15; word-break: break-word }
  .kuerzel { margin-top: 2mm; font-size: 10pt; color: #4b5b66; font-family: 'JetBrains Mono', monospace }
  .qr { flex: none; width: 29mm; text-align: center }
  .qr svg { width: 29mm; height: 29mm; display: block }
  .chip { margin-top: 1.2mm; font-family: 'JetBrains Mono', monospace; font-size: 8pt; letter-spacing: 0.12em }
`

export function renderCardsHtml(cards: PrintableCard[]): string {
  const pages = cards.map((c) => {
    const m = cardPrintModel(c)
    return `<section class="card">
  <div class="band"><b>SCHULKARTE</b><img src="/mammascanteen_logo.svg" alt="Mammas Canteen"></div>
  <div class="body">
    <div class="txt"><div class="name">${esc(m.bezeichnung)}</div><div class="kuerzel">${esc(m.kuerzel)}</div></div>
    <div class="qr">${qrSvg(m.qrContent)}<div class="chip">${esc(m.chip)}</div></div>
  </div>
</section>`
  }).join('\n')
  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>Schulkarten</title><style>${CSS}</style></head><body>${pages}</body></html>`
}

/** Öffnet den Druckdialog des Browsers für die Karten (eine Karte je Seite). */
export function printSchoolCards(cards: PrintableCard[]): void {
  if (cards.length === 0) return
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  frame.srcdoc = renderCardsHtml(cards)
  frame.onload = () => {
    const win = frame.contentWindow
    if (!win) { frame.remove(); return }
    const imgs = Array.from(win.document.images)
    Promise.all(imgs.map((i) => (i.complete ? null : new Promise((r) => { i.onload = i.onerror = r }))))
      .then(() => {
        win.focus()
        win.print()
        window.setTimeout(() => frame.remove(), 60_000)
      })
  }
  document.body.appendChild(frame)
}
